import type { Flight, FlightLeg, FlightSegment, QuoteRecord } from '../types';
import { airlineLogoUrl } from './airlineLogo';
import { airportCityLabel, airportCodeLabel } from './airportLabels';
import { segmentMealLabel } from './bookingFlightDisplay';
import { buildPassengerTicketTableHtml, escapeHtml } from './skyluxClientMailLayouts';
import { buildPaymentUrl } from './hosts';
import { passengerTicketRows } from './passengers';

const SKYLUX_HEADER_LOGO =
  'https://attachment-storage.skyluxtravel.com/92/31/d73df10503c451c2143f2abc51f2f091.svg';

type SegRow = {
  airline: string;
  flightNumber: string;
  departure: string;
  arrival: string;
  departureTime: string;
  arrivalTime: string;
  duration?: number;
  travelClass?: string;
};

function formatReservationDate(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return escapeHtml(iso);
    return escapeHtml(
      d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }),
    );
  } catch {
    return escapeHtml(iso);
  }
}

function formatTravelDayLabel(travelDate?: string): string {
  if (!travelDate?.trim()) return 'Travel day';
  try {
    const d = new Date(`${travelDate}T12:00:00Z`);
    if (Number.isNaN(d.getTime())) return escapeHtml(travelDate);
    return escapeHtml(d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' }));
  } catch {
    return escapeHtml(travelDate);
  }
}

function optionalDateUnderTime(travelDate?: string): string {
  if (!travelDate?.trim()) return '';
  try {
    const d = new Date(`${travelDate}T12:00:00Z`);
    if (Number.isNaN(d.getTime())) return `<div style="font: normal normal normal 12px/18px Arial;color: #000000;">${escapeHtml(travelDate)}</div>`;
    const line = escapeHtml(d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }));
    return `<div style="font: normal normal normal 12px/18px Arial;color: #000000;">${line}</div>`;
  } catch {
    return '';
  }
}

function minutesToDisplay(m?: number | null): string {
  if (m == null || !Number.isFinite(m)) return '—';
  const n = Math.max(0, Math.round(m));
  const h = Math.floor(n / 60);
  const min = n % 60;
  if (h <= 0) return `${min} min`;
  return `${h}h ${String(min).padStart(2, '0')}min`;
}

function normalizeLegSegments(leg: FlightLeg): SegRow[] {
  if (leg.segments && leg.segments.length > 0) {
    return leg.segments.map((s) => ({
      airline: s.airline,
      flightNumber: s.flightNumber,
      departure: s.departure,
      arrival: s.arrival,
      departureTime: s.departureTime,
      arrivalTime: s.arrivalTime,
      duration: s.duration,
      travelClass: s.travelClass,
    }));
  }
  return [
    {
      airline: leg.airline,
      flightNumber: leg.flightNumber,
      departure: leg.departure,
      arrival: leg.arrival,
      departureTime: leg.departureTime,
      arrivalTime: leg.arrivalTime,
      duration: leg.totalDuration,
      travelClass: undefined,
    },
  ];
}

function normalizeSegments(f: Flight): SegRow[] {
  return normalizeLegSegments(f);
}

function airportLines(code: string): { bold: string; sub: string; country: string } {
  const city = airportCityLabel(code);
  const iata = airportCodeLabel(code);
  return {
    bold: `${city.toUpperCase()}, ${iata}`,
    sub: `${city} Intl Arpt`,
    country: '—',
  };
}

function segmentBlockHtml(
  seg: SegRow,
  confirmation: string,
  dateUnderTimes: boolean,
  travelDate?: string,
  meal?: string | null,
): string {
  const logo = airlineLogoUrl(seg.flightNumber);
  const logoHtml = logo
    ? `<img src="${escapeHtml(logo)}" alt="" width="80" height="80" style="padding: 0 20px 0; width:80px; height:auto; object-fit:contain" />`
    : `<div style="padding: 0 20px 0; font: bold 14px/20px Arial; color: #000;">${escapeHtml(seg.airline)}</div>`;
  const dep = airportLines(seg.departure);
  const arr = airportLines(seg.arrival);
  const cls = seg.travelClass ? escapeHtml(seg.travelClass) : 'Economy';
  const conf = escapeHtml(confirmation || '—');
  const mealHtml = meal
    ? `<div style="display:inline-block;padding:0 20px 12px;font:normal normal normal 12px/18px Arial;color:#837F7F;">Meal options: <b style="color:#000;font:bold normal normal 12px/20px Arial;">${escapeHtml(meal)}</b></div>`
    : '';

  return `
<tr>
  <td style="border-top: 3px solid #4C24AC; color: #322C31;">
    <table border="0" cellpadding="0" cellspacing="0" width="100%" style="min-width: 100%; background: #fff; border-collapse: collapse;">
      <tr>
        <td style="border: 1px solid #c6d3de; border-top: 0; border-bottom:0;">
          <table border="0" cellpadding="0" cellspacing="0" width="100%" style="min-width: 100%; background: #fff; border-collapse: collapse;">
            <tr>
              <td style="vertical-align: top; min-width: 35%; width: 35%;">
                <table style="min-width: 100%; width: 100%; height:100%" border="0" cellpadding="0" cellspacing="0">
                  <tr>
                    <td class="logo-left" style="min-height: 120px;vertical-align:top; padding-top:13px;">
                      ${logoHtml}
                      <div style="display: inline-block; color: #837F7F; font: normal normal normal 12px/20px Arial; line-height: 16px; padding: 12px 20px 0;">
                        <b style="color: #000000; font: bold normal normal 12px/20px Arial;">Flight ${escapeHtml(seg.flightNumber)}</b><br>
                      </div>
                      <div style="display: inline-block; line-height: 16px; padding: 8px 20px 0;">
                        <b style="color: #000000; font: bold normal normal 12px/20px Arial;">Airline confirmation: ${conf}</b><br>
                      </div>
                      <div style="display: inline-block; color: #837F7F; font: normal normal normal 12px/20px Arial; line-height: 16px; padding: 12px 20px 0;">
                        <b style="color: #837F7F; font: normal normal normal 12px/20px Arial;">${cls}</b><br>
                      </div>
                      <div class="topPadding" style="display: inline-block; padding: 0 20px 12px; font: normal normal normal 12px/18px Arial; color: #837F7F;">Flight duration:
                        ${escapeHtml(minutesToDisplay(seg.duration))}
                      </div>
                      ${mealHtml}
                    </td>
                  </tr>
                </table>
              </td>
              <td style="vertical-align: top; min-width: 65%; width: 65%;">
                <table class="flight-info is-border" style="height: 100%;min-width: 100%; border-collapse: collapse;" border="0" cellpadding="0" cellspacing="0" width="100%">
                  <tr>
                    <td style="padding: 20px 0 18px 0;">
                      <table class="flight-info" style="min-width: 100%; border-collapse: collapse;" border="0" cellpadding="0" cellspacing="0" width="100%">
                        <tr>
                          <td style="width: 70%; padding: 0px 10px; display: block; color: #837F7F; font: normal normal normal 12px/18px Arial; line-height: 16px;">
                            <b style="color: #000000; text-transform: uppercase; font: normal normal bold 16px/20px Arial;">${escapeHtml(dep.bold)}</b><br>
                            ${escapeHtml(dep.sub)}<br>
                            ${escapeHtml(dep.country)}
                          </td>
                          <td style="width:30%; color: #67677d; font-size: 12px; line-height: 16px; vertical-align: top;">
                            <b style="color: #000000; text-transform: uppercase; font: normal normal bold 16px/20px Arial;">${escapeHtml(seg.departureTime || '—')}</b><br>
                            ${dateUnderTimes ? optionalDateUnderTime(travelDate) : ''}
                          </td>
                        </tr>
                      </table>
                    </td>
                  </tr>
                  <tr>
                    <td style="padding: 18px 0 13px;">
                      <table class="flight-info" style="min-width: 100%; border-collapse: collapse;" border="0" cellpadding="0" cellspacing="0" width="100%">
                        <tr>
                          <td style="width:70%; padding: 0px 10px; display: block; color: #837F7F; font: normal normal normal 12px/18px Arial; line-height: 16px;">
                            <b style="color: #000000; text-transform: uppercase; font: normal normal bold 16px/20px Arial;">${escapeHtml(arr.bold)}</b><br>
                            ${escapeHtml(arr.sub)}<br>
                            ${escapeHtml(arr.country)}
                          </td>
                          <td style="width:30%; color: #67677d; font-size: 12px; line-height: 16px; vertical-align:top;">
                            <b style="color: #000000; text-transform: uppercase; font: normal normal bold 16px/20px Arial;">${escapeHtml(seg.arrivalTime || '—')}</b><br>
                            ${dateUnderTimes ? optionalDateUnderTime(travelDate) : ''}
                          </td>
                        </tr>
                      </table>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  </td>
</tr>`;
}

function itineraryDayHeaderHtml(travelDay: string, destLabel: string, totalDur: string, directionLabel?: string): string {
  const dir = directionLabel ? `${escapeHtml(directionLabel)} — ` : '';
  return `
        <tr>
          <td>
            <table border="0" cellpadding="0" cellspacing="0" width="100%" style="min-width: 100%; background: #fff; border-collapse: collapse;">
              <tr>
                <td class="text-mobile" style="padding: 10px 20px;border: 1px solid #c6d3de; border-bottom: 0;">
                  <span style="display: inline-block; width: 50%; min-width: 50%; color: #000000; font: normal normal normal 14px/24px Arial;"><b>${dir}${travelDay}</b> — ${destLabel}</span>
                  <span style="text-align: right; display: inline-block; width: 49%; min-width: 49%; color: #837F7F; font: normal normal normal 12px/24px Arial;">Total travel time <b>${escapeHtml(totalDur)}</b></span>
                </td>
              </tr>
            </table>
          </td>
        </tr>`;
}

function appendLegItinerary(
  parts: string[],
  leg: FlightLeg,
  confirmation: string,
  directionLabel?: string,
): void {
  const segs = normalizeLegSegments(leg);
  const travelDay = formatTravelDayLabel(leg.travelDate);
  const totalDur = minutesToDisplay(leg.totalDuration);
  const destLabel = escapeHtml((segs[segs.length - 1]?.arrival || leg.arrival || 'Destination').toUpperCase());
  parts.push(itineraryDayHeaderHtml(travelDay, destLabel, totalDur, directionLabel));

  const layovers = leg.layovers || [];
  for (let i = 0; i < segs.length; i += 1) {
    const seg = segs[i]!;
    const segForMeal: FlightSegment = {
      airline: seg.airline,
      flightNumber: seg.flightNumber,
      departure: seg.departure,
      arrival: seg.arrival,
      departureTime: seg.departureTime,
      arrivalTime: seg.arrivalTime,
      duration: seg.duration,
      travelClass: seg.travelClass,
    };
    const dateUnder = i === 0;
    parts.push(
      segmentBlockHtml(seg, confirmation, dateUnder, leg.travelDate, segmentMealLabel(segForMeal)),
    );
    if (i < segs.length - 1 && layovers[i]) {
      const ly = layovers[i]!;
      parts.push(layoverRowHtml(ly.name || ly.id, ly.duration, ly.overnight));
    }
  }
}

function layoverRowHtml(name: string, durationMin: number, overnight?: boolean): string {
  const extra = overnight ? ' (overnight)' : '';
  const text = `${minutesToDisplay(durationMin)} layover in ${name}${extra}`;
  return `
<tr>
  <td style="background: #F4F4F4; text-align: center; font-size: 12px; font-weight: bold; padding: 10px 0; height: 20px; border: 1px solid #c6d3de; border-top: 0; border-bottom: 0;" valign="middle">
    <span style="color: #000000; font: normal normal normal 12px/20px Arial; vertical-align: middle;">${escapeHtml(text)}</span>
  </td>
</tr>`;
}

/**
 * Inner HTML (no DOCTYPE) aligned with `aditional/viewticket.html` for embedding on the booking / view-ticket surface.
 */
export type ViewTicketHtmlOptions = {
  /** `ticket` — public e-ticket page; omit payment prompts. */
  mode?: 'ticket' | 'embed';
};

export function buildViewTicketInnerHtml(quote: QuoteRecord, options?: ViewTicketHtmlOptions): string {
  const mode = options?.mode ?? 'embed';
  const f = quote.flight;
  const confirmation = quote.confirmationNumber || '—';
  const ticketRows = passengerTicketRows(quote);
  const ticketNosHtml = ticketRows.map((row) => escapeHtml(row.eTicketNumber)).join('<br>');
  const passengerTableHtml = buildPassengerTicketTableHtml(ticketRows);
  const resDate = formatReservationDate(quote.createdAt);
  const logoHeader = escapeHtml(SKYLUX_HEADER_LOGO);

  const parts: string[] = [];

  parts.push(`
<table border="0" cellpadding="0" cellspacing="0" width="100%" style="min-width: 100%; background-color:#F4F4F4; height:30px">
  <tr>
    <td align="center">
      <table align="center" cellpadding="0" cellspacing="0" style="min-width:608px; width:608px; font-family: Arial, sans-serif; font-size: 14px; color: #717171; border-collapse: collapse;">
        <tr>
          <td>
            <table align="left" border="0" cellpadding="0" cellspacing="0" width="auto" style="min-width: 35%;padding-left: 10px;">
              <tr>
                <td style="padding-top: 10px;">
                  <span style="display: block; font-size: 10px; height: 13px; line-height: 13px; color: #797979;">General Lines</span>
                </td>
              </tr>
            </table>
            <table align="right" border="0" cellpadding="0" cellspacing="0" width="250px" style="min-width: 20%;">
              <tr>
                <td style="padding: 10px;text-align:right;" valign="bottom">
                  <span style="color: #797979; font-size: 10px;">(US/CA) <a href="tel:+18555079770" style="color: #797979; text-decoration: none;">1-855-507-9770</a></span>
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>
<table border="0" cellpadding="0" cellspacing="0" width="100%" style="min-width: 100%; background-color:#4C24AC; height:56px">
  <tr>
    <td align="center">
      <table align="center" cellpadding="0" cellspacing="0" style="min-width:608px; width:608px; font-family: Arial, sans-serif; font-size: 14px; color: #717171; border-collapse: collapse;">
        <tr>
          <td>
            <table align="left" border="0" cellpadding="0" cellspacing="0" width="auto" style="min-width: 35%;padding-left: 10px;">
              <tr>
                <td style="padding-top: 10px;">
                  <img src="${logoHeader}" alt="SkyLux Travel" width="280" height="48" style="display:block;max-width:280px;width:100%;height:auto;border:0" />
                </td>
              </tr>
            </table>
            <table align="right" border="0" cellpadding="0" cellspacing="0" width="250px" style="min-width: 20%;">
              <tr>
                <td style="padding: 10px; text-align:right; font: normal normal normal 12px/18px Arial; color: #FFFFFF;" valign="bottom">
                  <span>(USA/Canada) <span style="text-transform: uppercase;"><a href="tel:+18889852261" style="color: #FFFFFF; text-decoration: none;">(888) 985-2261</a></span></span>
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>
<table border="0" cellpadding="0" cellspacing="0" width="100%" style="min-width:100%; background:#ffffff">
  <tr>
    <td align="center" style="padding:0 12px 24px">
      <table align="center" cellpadding="0" cellspacing="0" style="min-width:600px; max-width:608px; width:100%; font-family: Arial, sans-serif; font-size: 14px; color: #717171; border-collapse: collapse;">
        <tr>
          <td style="padding-top: 25px">
            <table style="min-width: 100%;" align="center" cellpadding="0" cellspacing="0" width="100%">
              <tr valign="middle" height="15" style="font: normal normal normal 10px/20px Arial;color: #837F7F;text-align: center;">
                <td width="33%" style="border-right: 1px solid #c6d3de;"><span>Confirmation Number:</span></td>
                <td width="33%"><span>Reservation Date:</span></td>
                <td width="33%" style="border-left: 1px solid #c6d3de;"><span>Ticket number(s):</span></td>
              </tr>
              <tr valign="middle" height="30" style="font: normal normal normal 20px/20px Arial; color: #000000; text-align: center;">
                <td width="33%" style="border-right: 1px solid #c6d3de;"><span style="text-transform: uppercase;">${escapeHtml(confirmation)}</span></td>
                <td width="33%"><span style="text-transform: uppercase;">${resDate}</span></td>
                <td width="33%" style="border-left: 1px solid #c6d3de;"><span style="text-transform: uppercase;">${ticketNosHtml}</span></td>
              </tr>
            </table>
          </td>
        </tr>
        ${
          mode === 'ticket'
            ? `<tr>
          <td style="padding: 4px 0 0">${passengerTableHtml}</td>
        </tr>`
            : ''
        }
        <tr>
          <td class="center-text" style="text-align: center; color: #000000; font: normal normal normal 16px/24px Arial; display: block; padding: 22px 20px 10px;">Itinerary Details</td>
        </tr>`);

  appendLegItinerary(parts, f, confirmation, f.returnLeg ? 'Outbound' : undefined);
  if (f.returnLeg) {
    appendLegItinerary(parts, f.returnLeg, confirmation, 'Return');
  }

  const airlineName = escapeHtml(f.airline || 'your airline');
  parts.push(`
        <tr>
          <td class="center-text" style="text-align: center; color: #000000; font: normal normal normal 16px/24px Arial; display: block; padding: 18px 20px 8px;">Baggage</td>
        </tr>
        <tr>
          <td class="center-text" style="padding: 0 20px 28px; color: #34495E;">
            <span style="line-height: 18px; font: normal normal normal 12px/18px Arial; color: #837F7F;">Please Note: Airline baggage fees are not included and will apply. For specific baggage allowance and fees, contact ${airlineName} or see their published baggage rules.</span>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>`);

  if (mode === 'embed' && quote.paymentStatus === 'unpaid') {
    const pay = escapeHtml(buildPaymentUrl(quote.id));
    parts.push(`
<table border="0" cellpadding="0" cellspacing="0" width="100%" style="min-width:100%; background:#f8fafc">
  <tr>
    <td align="center" style="padding: 16px 12px 32px">
      <a href="${pay}" style="display:inline-block;background:#4a25ac;color:#fff;font:bold 14px/1 Arial,sans-serif;padding:14px 28px;border-radius:6px;text-decoration:none">Complete payment</a>
      <div style="font:12px/1.4 Arial,sans-serif;color:#64748b;margin-top:10px">Opens secure payment for this quote.</div>
    </td>
  </tr>
</table>`);
  } else if (mode === 'embed') {
    parts.push(`
<table border="0" cellpadding="0" cellspacing="0" width="100%" style="min-width:100%; background:#ecfdf5">
  <tr>
    <td align="center" style="padding: 14px 12px 28px; font: 13px/1.5 Arial,sans-serif; color:#065f46">
      <strong>Paid</strong> — Thank you. Keep this itinerary for your records.
    </td>
  </tr>
</table>`);
  }

  return parts.join('\n');
}
